import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { ActiveModelStatus } from '../../api/grpc/revitClient';

export interface DocumentWriteGuardDialogProps {
  open: boolean;
  blockReason: 'no-document' | 'central-inaccessible' | 'read-only' | null;
  activeModelStatus?: ActiveModelStatus | null;
  supportingFlags?: string[];
  onRecheck: () => void | Promise<void>;
  onDismiss?: () => void;
  title?: string;
}

const DocumentWriteGuardDialog: React.FC<DocumentWriteGuardDialogProps> = ({
  open,
  blockReason,
  activeModelStatus,
  supportingFlags = [],
  onRecheck,
  onDismiss,
  title,
}) => {
  const documentTitle = activeModelStatus?.documentTitle ?? 'the active document';

  const defaultTitle =
    blockReason === 'no-document'
      ? 'No document is open'
      : blockReason === 'central-inaccessible'
        ? 'Central model is not accessible'
        : 'Model is read-only';

  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick') {
      return;
    }
    onDismiss?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="document-write-guard-title"
      aria-describedby="document-write-guard-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="document-write-guard-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorOutlineIcon color="error" />
        {title ?? defaultTitle}
      </DialogTitle>
      <DialogContent>
        {blockReason === 'no-document' && (
          <DialogContentText id="document-write-guard-description">
            No project is currently open. This tool writes parameter values into the active model,
            so it cannot run without one. Open a project and click <strong>Recheck model</strong>.
          </DialogContentText>
        )}

        {blockReason === 'central-inaccessible' && (
          <DialogContentText id="document-write-guard-description" component="div">
            <Typography component="p" sx={{ mb: 1 }}>
              <strong>{documentTitle}</strong> is a workshared local model, but the central model is
              not reachable
              {activeModelStatus?.centralModelPath ? (
                <>
                  {' '}at <code>{activeModelStatus.centralModelPath}</code>
                </>
              ) : (
                ' (no central model path is available)'
              )}
              .
            </Typography>
            <Typography component="p" sx={{ mb: 1 }}>
              Edits are blocked until the central model is available. To fix this, reopen the model
              from central or recreate it as a new central model, then click{' '}
              <strong>Recheck model</strong>.
            </Typography>
          </DialogContentText>
        )}

        {blockReason === 'read-only' && (
          <DialogContentText id="document-write-guard-description" component="div">
            <Typography component="p" sx={{ mb: 1 }}>
              <strong>{documentTitle}</strong> is opened read-only. The model cannot be modified,
              so no parameter values can be written.
            </Typography>
            {supportingFlags.length > 0 && (
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                {supportingFlags.map((flag, index) => (
                  <li key={index}>
                    <Typography component="span">{flag}</Typography>
                  </li>
                ))}
              </Box>
            )}
            <Typography component="p" sx={{ mt: 1 }}>
              To unblock, reopen the model with write access and then click{' '}
              <strong>Recheck model</strong>.
            </Typography>
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {onDismiss && (
          <Button onClick={onDismiss} color="inherit">
            Close
          </Button>
        )}
        <Button onClick={() => void onRecheck()} variant="contained" autoFocus>
          Recheck model
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentWriteGuardDialog;
